import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChartBarIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  GlobeAltIcon,
  ClockIcon,
  MapPinIcon,
  AcademicCapIcon,
  HandRaisedIcon,
  TrophyIcon,
  StarIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  UsersIcon,
  CogIcon,
  LightBulbIcon,
  HeartIcon,
  SparklesIcon,
  ScaleIcon,
  MagnifyingGlassIcon,
  ClipboardDocumentListIcon
} from '@heroicons/react/24/outline';
import HomeNav from '../navigations/homenav';
import Footer from '../navigations/Footer';
import { useLanguageContext } from '../navigations/homenav';

// Program Card Component
const ProgramCard = ({ icon, title, description, activities, category, impact, onLearnMore }) => {
  const { t } = useLanguageContext();
  return (
  <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 group hover:-translate-y-2 border border-gray-100 overflow-hidden">
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center justify-center h-16 w-16 rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-500 text-white group-hover:scale-110 transition-transform duration-300">
          {icon}
        </div>
        <span className="px-3 py-1 bg-teal-100 text-teal-700 text-sm font-medium rounded-full">
          {category}
        </span>
      </div>

      <div className="mb-4">
        <div className="inline-flex items-center px-3 py-1 bg-green-100 text-green-700 text-sm font-semibold rounded-full">
          <SparklesIcon className="h-4 w-4 mr-1" />
          {t('programsImpactLabel')}: {impact}
        </div>
      </div>

      <h3 className="text-2xl font-bold text-gray-800 mb-4 group-hover:text-teal-600 transition-colors duration-300">
        {title}
      </h3>

      <p className="text-gray-600 leading-relaxed mb-6">
        {(() => {
          const text = description || '';
          const short = text.length > 200 ? `${text.slice(0, 200)}…` : text;
          return short;
        })()}
      </p>

      <div>
        <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
          <CheckCircleIcon className="h-5 w-5 mr-2 text-teal-500" />
          {t('programsKeyActivitiesLabel')}:
        </h4>
        <ul className="space-y-2">
          {activities.map((activity, index) => (
            <li key={index} className="flex items-start text-gray-600">
              <span className="text-teal-500 mr-3 text-sm">•</span>
              <span className="text-sm">{activity}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>

    <div className="px-8 pb-8">
      <button onClick={onLearnMore} className="w-full px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-semibold rounded-xl hover:from-teal-600 hover:to-cyan-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105">
        {t('learnMore')}
      </button>
    </div>
  </div>
  );
};

// Filter Tag Component
const FilterTag = ({ label, active, onClick }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
      active
        ? 'bg-teal-500 text-white shadow-lg'
        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
    }`}
  >
    {label}
  </button>
);

const ProgramsPage = () => {
  const { t } = useLanguageContext();
  const [activeFilter, setActiveFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [categories, setCategories] = useState(['All']);
  const [programs, setPrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // map optional icon names from backend to Heroicons
  const iconMap = {
    ScaleIcon: <ScaleIcon className="h-8 w-8" />,
    LightBulbIcon: <LightBulbIcon className="h-8 w-8" />,
    GlobeAltIcon: <GlobeAltIcon className="h-8 w-8" />,
    UserGroupIcon: <UserGroupIcon className="h-8 w-8" />,
    AcademicCapIcon: <AcademicCapIcon className="h-8 w-8" />,
    UsersIcon: <UsersIcon className="h-8 w-8" />,
    HeartIcon: <HeartIcon className="h-8 w-8" />,
    CogIcon: <CogIcon className="h-8 w-8" />,
    ChartBarIcon: <ChartBarIcon className="h-8 w-8" />,
    MapPinIcon: <MapPinIcon className="h-8 w-8" />,
    ClockIcon: <ClockIcon className="h-8 w-8" />,
    TrophyIcon: <TrophyIcon className="h-8 w-8" />,
    StarIcon: <StarIcon className="h-8 w-8" />,
    SparklesIcon: <SparklesIcon className="h-8 w-8" />,
    ClipboardDocumentListIcon: <ClipboardDocumentListIcon className="h-8 w-8" />,
  };

  useEffect(() => {
    const fallbackCategories = ['All', 'Advocacy', 'Education', 'Infrastructure', 'Community', 'Research', 'Youth'];
    const loadCategories = async () => {
      try {
        const res = await fetch('https://aaapd.org.et/gallery_backend/api/getProgramCategories.php');
        if (!res.ok) throw new Error('Failed to load categories');
        const data = await res.json();
        // Expecting an array of category strings or objects with name
        let cats = Array.isArray(data)
          ? data.map((c) => (typeof c === 'string' ? c : c.name)).filter(Boolean)
          : [];
        cats = Array.from(new Set(cats));
        if (cats.length === 0) throw new Error('Empty categories');
        setCategories(['All', ...cats]);
      } catch (e) {
        setCategories(fallbackCategories);
      }
    };
    loadCategories();
  }, []);

  // load programs from backend
  useEffect(() => {
    const loadPrograms = async () => {
      try {
        const res = await fetch('https://aaapd.org.et/gallery_backend/api/getPrograms.php');
        if (!res.ok) throw new Error('Failed to load programs');
        const data = await res.json(); // expect array of {title, category, impact, description, activities, icon}

        const normalized = Array.isArray(data) ? data.map((p) => {
          // parse activities: try JSON array, else split by commas
          let acts = [];
          if (Array.isArray(p.activities)) {
            acts = p.activities;
          } else if (typeof p.activities === 'string') {
            try {
              const parsed = JSON.parse(p.activities);
              acts = Array.isArray(parsed) ? parsed : p.activities.split(',');
            } catch (_) {
              acts = p.activities.split(',');
            }
            acts = acts.map(a => String(a).trim()).filter(Boolean);
          }

          // Prefer a Programs-related icon by default and when legacy 'ScaleIcon' is present
          const iconEl = (p.icon === 'ScaleIcon')
            ? <ClipboardDocumentListIcon className="h-8 w-8" />
            : (p.icon && iconMap[p.icon])
              ? iconMap[p.icon]
              : <ClipboardDocumentListIcon className="h-8 w-8" />;

          return {
            icon: iconEl,
            title: p.title || '',
            category: p.category || 'General',
            impact: p.impact || '',
            description: p.description || '',
            activities: acts.length ? acts : ['Learn more from our team'],
          };
        }) : [];

        setPrograms(normalized);
      } catch (e) {
        setPrograms([]);
      }
    };
    loadPrograms();
  }, []);

  // categories now loaded from backend (with fallback)

  const filteredPrograms = programs.filter(program => {
    const matchesCategory = activeFilter === 'All' || program.category === activeFilter;
    const matchesSearch = searchTerm === '' ||
      program.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      program.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      program.activities.some(activity => activity.toLowerCase().includes(searchTerm.toLowerCase()));

    return matchesCategory && matchesSearch;
  });

  return (
    <div className="bg-gray-50">
      <HomeNav />

      <main>
        {/* Hero Section */}
        <section
          className="relative py-24 md:py-32 bg-cover bg-center text-white"
          style={{ backgroundImage: "url('/programsimage4.jpg')" }}
        >
          <div className="absolute inset-0 bg-slate-800 bg-opacity-70"></div>
          <div className="relative max-w-6xl mx-auto px-6 text-center z-10">
            <SparklesIcon className="h-20 w-20 mx-auto mb-8 text-teal-400" />
            <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight">
              {t('programsPageTitle')}
            </h1>
            <p className="mt-6 text-lg md:text-xl text-teal-100 max-w-4xl mx-auto leading-relaxed">
              {t('programsPageSubtitle')}
            </p>
          </div>
        </section>

        <section className="py-12 bg-gray-50">
          <div className="max-w-6xl mx-auto px-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-8">
              <div className="text-center md:text-left">
                <h2 className="text-2xl font-bold text-gray-800 mb-1">{t('programsBrowseByCategory')}</h2>
                <p className="text-gray-600">{t('programsClickCategoryToFilter')}</p>
              </div>
              <div className="relative w-full max-w-md md:w-80">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('programsSearchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex flex-wrap justify-center gap-3">
              {categories.map((category) => (
                <FilterTag
                  key={category}
                  label={category === 'All' ? t('programsAll') : category}
                  active={activeFilter === category}
                  onClick={() => setActiveFilter(category)}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Programs Grid Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPrograms.map((program, index) => (
                <ProgramCard
                  key={index}
                  {...program}
                  onLearnMore={() => { setSelectedProgram(program); setShowModal(true); }}
                />
              ))}
            </div>

            {filteredPrograms.length === 0 && (
              <div className="text-center py-16">
                <div className="text-6xl mb-4">🔍</div>
                <h3 className="text-2xl font-bold text-gray-800 mb-2">{t('programsEmptyTitle')}</h3>
                <p className="text-gray-600">{t('programsEmptyDesc')}</p>
              </div>
            )}
          </div>
        </section>

        {/* Programs Tagline Section */}
        <section className="py-20 bg-gradient-to-r from-teal-600 via-cyan-600 to-teal-600 text-white">
          <div className="max-w-6xl mx-auto px-6 text-center">
            <div className="bg-white bg-opacity-10 p-12 rounded-3xl backdrop-blur-sm">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                {t('programsTaglineTitle')}
              </h2>
              <p className="text-xl text-teal-100 leading-relaxed">
                {t('programsTaglineSub')}
              </p>
            </div>
          </div>
        </section>

        {/* Programs Infographics Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">

            {/* Program Flow Infographic */}
            <div className="bg-gradient-to-r from-gray-300 via-gray-200 to-gray-300 rounded-3xl p-8 mb-16 text-gray-800">
              <h3 className="text-2xl font-bold text-center mb-8 text-gray-700">{t('programsFlowTitle')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="w-20 h-20 bg-gray-400 rounded-full flex items-center justify-center mx-auto mb-4">
                    <UserGroupIcon className="h-10 w-10 text-white" />
                  </div>
                  <h4 className="font-bold mb-2">1. {t('programsFlow1Title')}</h4>
                  <p className="text-sm text-gray-600">{t('programsFlow1Desc')}</p>
                </div>

                <div className="text-center relative">
                  <ArrowRightIcon className="absolute top-8 left-0 w-8 h-8 text-gray-500 hidden md:block" style={{ transform: 'translateX(-100%)' }} />
                  <div className="w-20 h-20 bg-gray-400 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AcademicCapIcon className="h-10 w-10 text-white" />
                  </div>
                  <h4 className="font-bold mb-2">2. {t('programsFlow2Title')}</h4>
                  <p className="text-sm text-gray-600">{t('programsFlow2Desc')}</p>
                </div>

                <div className="text-center relative">
                  <ArrowRightIcon className="absolute top-8 left-0 w-8 h-8 text-gray-500 hidden md:block" style={{ transform: 'translateX(-100%)' }} />
                  <div className="w-20 h-20 bg-gray-400 rounded-full flex items-center justify-center mx-auto mb-4">
                    <HeartIcon className="h-10 w-10 text-white" />
                  </div>
                  <h4 className="font-bold mb-2">3. {t('programsFlow3Title')}</h4>
                  <p className="text-sm text-gray-600">{t('programsFlow3Desc')}</p>
                </div>

                <div className="text-center relative">
                  <ArrowRightIcon className="absolute top-8 left-0 w-8 h-8 text-gray-500 hidden md:block" style={{ transform: 'translateX(-100%)' }} />
                  <div className="w-20 h-20 bg-gray-400 rounded-full flex items-center justify-center mx-auto mb-4">
                    <TrophyIcon className="h-10 w-10 text-white" />
                  </div>
                  <h4 className="font-bold mb-2">4. {t('programsFlow4Title')}</h4>
                  <p className="text-sm text-gray-600">{t('programsFlow4Desc')}</p>
                </div>
              </div>
            </div>

            {/* Accessibility & Inclusion Services */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">{t('programsAccessInclusionTitle')}</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CogIcon className="h-8 w-8 text-blue-600" />
                  </div>
                  <h4 className="font-bold text-gray-800 mb-2">{t('programsAccessItem1Title')}</h4>
                  <p className="text-sm text-gray-600">{t('programsAccessItem1Desc')}</p>
                </div>

                <div className="text-center">
                  <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <LightBulbIcon className="h-8 w-8 text-yellow-600" />
                  </div>
                  <h4 className="font-bold text-gray-800 mb-2">{t('programsAccessItem2Title')}</h4>
                  <p className="text-sm text-gray-600">{t('programsAccessItem2Desc')}</p>
                </div>

                <div className="text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <BuildingOfficeIcon className="h-8 w-8 text-blue-600" />
                  </div>
                  <h4 className="font-bold text-gray-800 mb-2">{t('programsAccessItem3Title')}</h4>
                  <p className="text-sm text-gray-600">{t('programsAccessItem3Desc')}</p>
                </div>

                <div className="text-center">
                  <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <ScaleIcon className="h-8 w-8 text-yellow-600" />
                  </div>
                  <h4 className="font-bold text-gray-800 mb-2">{t('programsAccessItem4Title')}</h4>
                  <p className="text-sm text-gray-600">{t('programsAccessItem4Desc')}</p>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="py-20 bg-gray-50">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {t('getInvolved')}
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                {t('howToAccessDesc')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <div className="text-center bg-white p-8 rounded-2xl shadow-lg">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-teal-600">1</span>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-4">{t('step1Title')}</h3>
                <p className="text-gray-600">{t('step1Desc')}</p>
              </div>

              <div className="text-center bg-white p-8 rounded-2xl shadow-lg">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-teal-600">2</span>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-4">{t('contactUs')}</h3>
                <p className="text-gray-600">{t('programsStep2Desc')}</p>
              </div>

              <div className="text-center bg-white p-8 rounded-2xl shadow-lg">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-teal-600">3</span>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-4">{t('programsGetStartedTitle')}</h3>
                <p className="text-gray-600">{t('programsGetStartedDesc')}</p>
              </div>
            </div>

            <div className="text-center">
              <Link
                to="/contactus"
                className="inline-block px-8 py-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-bold rounded-xl hover:from-teal-600 hover:to-cyan-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                {t('programsStartJourney')}
              </Link>
            </div>
          </div>
        </section>
      </main>

      {showModal && selectedProgram && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-900">{selectedProgram.title}</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600" aria-label="Close">
                ×
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <span className="px-3 py-1 bg-teal-100 text-teal-700 text-sm font-medium rounded-full">{selectedProgram.category}</span>
                {selectedProgram.impact && (
                  <span className="inline-flex items-center px-3 py-1 bg-green-100 text-green-700 text-sm font-semibold rounded-full">
                    <SparklesIcon className="h-4 w-4 mr-1" /> {t('programsImpactLabel')}: {selectedProgram.impact}
                  </span>
                )}
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-1">{t('descriptionLabel')}</h3>
                <p className="text-gray-700 whitespace-pre-wrap">{selectedProgram.description || '-'}</p>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                  <CheckCircleIcon className="h-5 w-5 mr-2 text-teal-500" /> {t('programsKeyActivitiesLabel')}
                </h4>
                <ul className="list-disc pl-5 space-y-1 text-gray-700">
                  {(selectedProgram.activities || []).map((a, i) => (
                    <li key={i}>{a}</li>
                  ))}
                </ul>
              </div>
              <div className="pt-2 flex justify-end">
                <button onClick={() => setShowModal(false)} className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-800 hover:bg-gray-50">{t('closeButton')}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ProgramsPage;
